// Currency formatting utilities for Nigerian Naira

/**
 * Format a number as Nigerian Naira
 * @param amount - The amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export function formatNaira(
  amount: number,
  options: {
    showSymbol?: boolean
    showDecimals?: boolean
    compact?: boolean
  } = {},
): string {
  const { showSymbol = true, showDecimals = false, compact = false } = options

  if (compact && amount >= 1000000) {
    const millions = amount / 1000000
    const formatted = millions.toFixed(millions % 1 === 0 ? 0 : 1)
    return showSymbol ? `₦${formatted}M` : `${formatted}M`
  }

  if (compact && amount >= 1000) {
    const thousands = amount / 1000
    const formatted = thousands.toFixed(thousands % 1 === 0 ? 0 : 1)
    return showSymbol ? `₦${formatted}K` : `${formatted}K`
  }

  const formatter = new Intl.NumberFormat('en-NG', {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  })

  const formatted = formatter.format(amount)
  return showSymbol ? `₦${formatted}` : formatted
}

/**
 * Parse a currency string to number
 * @param value - The currency string to parse
 * @returns The numeric value
 */
export function parseNaira(value: string): number {
  // Remove currency symbol, commas, and spaces
  const cleaned = value.replace(/[₦,\s]/g, '')
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Format a number with proper thousand separators
 * @param value - The number to format
 * @returns Formatted number string
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-NG').format(value)
}

/**
 * Calculate percentage change between two values
 * @param current - Current value
 * @param previous - Previous value
 * @returns Percentage change
 */
export function calculatePercentageChange(
  current: number,
  previous: number,
): number {
  if (previous === 0) return current > 0 ? 100 : 0
  return ((current - previous) / previous) * 100
}

/**
 * Format percentage with sign
 * @param value - The percentage value
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number): string {
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}%`
}
